import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useViewAs } from '../contexts/ViewAsContext';

/**
 * Portal logins with their account links, for the staff "view as" picker.
 * Backed by portal_users_view (security_invoker): customers only ever see
 * their own row, staff see everyone.
 */
export interface PortalUser {
  user_id: string;
  email: string;
  full_name: string | null;
  profile_role: string | null;
  active_accounts: number;
  invited_accounts: number;
  last_login_at: string | null;
  account_names: string[];
}

export function usePortalUsers() {
  const { isStaff } = useViewAs();
  return useQuery({
    queryKey: ['portal-users'],
    enabled: isStaff,
    queryFn: async (): Promise<PortalUser[]> => {
      const { data, error } = await supabase
        .from('portal_users_view')
        .select('user_id, email, full_name, profile_role, active_accounts, invited_accounts, last_login_at, account_names')
        .order('email');
      if (error) throw error;
      return (data ?? [])
        .filter((row) => !!row.user_id && !!row.email)
        .map((row) => ({
          user_id: row.user_id!,
          email: row.email!,
          full_name: row.full_name,
          profile_role: row.profile_role,
          active_accounts: row.active_accounts ?? 0,
          invited_accounts: row.invited_accounts ?? 0,
          last_login_at: row.last_login_at,
          account_names: row.account_names ?? [],
        }));
    },
    staleTime: 60 * 1000,
  });
}
